import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { MatSelect } from '@angular/material/select';
import { ActivatedRoute } from '@angular/router';
import { TimeOffApprovalFlow } from '@app/models/time-off-v3/time-off-approval-flow.model';
import { TimeOffPolicy } from '@app/models/time-off-v3/time-off-policy.model';
import { TimeOffType } from '@app/models/time-off-v3/time-off-type.model';
import {
    AccrualFrequencies,
    DECIMAL_PLACES,
    TimeOffDateFormatDashes,
} from '@app/modules/time-off-v3/meta/time-off-meta';
import { TitlecasePipe } from '@app/pipes';
import { TranslatableKey } from '@app/types/translatable.type';
import {
    TimeOffTypeUnitOfTimeDisplayOptions,
    TimeOffTypeUnitOfTimeHeaderDisplayOptions,
} from '@app/types/translatables/time-off-v3-display.options';
import { BaseForm } from '@forms/base.form';
import { TimeOffRequestRules } from '@models/time-off-v3/time-off-request-rules.model';
import { getTime, setTime } from '@time-off-v3/functions/time-unit-converter';
import { PolicyFormTooltips } from '@time-off-v3/meta/tooltip-meta';
import moment from 'moment';
import { TimeOffPreviews } from '../../interfaces/time-off-balance.interface';
import { TimeOffService } from '../../services';

type ActionType = 'update' | 'create';

@Component({
    selector: 'app-time-off-policy-form',
    templateUrl: './policy.form.html',
    styleUrls: ['./policy.form.scss'],
})
export class PolicyForm extends BaseForm implements OnInit {
    @Input() timeOffPolicy: TimeOffPolicy;
    @Input() timeOffTypes: TimeOffType[];
    @Input() timeOffApprovalFlows: TimeOffApprovalFlow[];
    @Input() actionType: ActionType = 'create';
    @Input() requestRule: TimeOffRequestRules;
    @ViewChild('accrualTiming') accrualTiming: MatSelect;
    timeOffTypeId: number;
    accrualFrequencies = AccrualFrequencies;
    editForm = false;
    daysPerPeriod = '';
    accrualPreviews: TimeOffPreviews[] = [];
    numberOfPreviews = 3;
    policyFormToolTips = PolicyFormTooltips;
    days = 0;
    titleCasePipe = new TitlecasePipe();

    constructor(
        private timeOffService: TimeOffService,
        private route: ActivatedRoute
    ) {
        super();
    }

    get unitOfTimeHeader(): string | TranslatableKey {
        const unit = this.timeOffPolicy?.timeOffType?.unitOfTimePlural;
        return TimeOffTypeUnitOfTimeHeaderDisplayOptions[unit] ?? this.titleCasePipe.transform(unit) + 'Accrued / Year';
    }

    get unitOfTime(): string | TranslatableKey {
        const unit = this.timeOffPolicy?.timeOffType?.unitOfTimePlural;
        return TimeOffTypeUnitOfTimeDisplayOptions[unit] ?? this.titleCasePipe.transform(unit);
    }

    ngOnInit(): void {
        this.timeOffTypeId = this.route.snapshot.queryParams.timeOffTypeId
            ? +this.route.snapshot.queryParams.timeOffTypeId
            : this.timeOffPolicy.timeOffTypeId;

        this.setTimeOffType(this.timeOffTypeId);

        if (this.actionType === 'create') {
            return;
        }

        this.editForm = true;
        this.getAccrualPreview(this.timeOffPolicy.accrualFrequency);
        this.setDaysPerYear();
        this.calculateDaysForPeriod();
    }

    calculateDaysForPeriod(): void {
        this.daysPerPeriod = '0';
        // Round to 3 decimal places because of business requirements.
        if (this.days !== null && this.timeOffPolicy.accrualFrequency) {
            const daysPerPeriod = this.days / this.accrualFrequencies[this.timeOffPolicy.accrualFrequency];

            this.timeOffPolicy.daysAccruedPerPeriod = setTime(daysPerPeriod, this.timeOffPolicy?.timeOffType);
            this.daysPerPeriod = daysPerPeriod.toFixed(DECIMAL_PLACES);
        }
    }

    getAccrualPreview(frequency: string): void {
        if (this.timeOffPolicy.hasAccrualValuesSet(frequency)) {
            this.timeOffService.getAccrualPreview(this.timeOffPolicy).then((accrualPreviews: TimeOffPreviews[]) => {
                this.accrualPreviews = accrualPreviews.slice(0, this.numberOfPreviews);
            });
        }
    }

    getAccrualHappenOnDate(startDate: string, endDate: string): string {
        if (this.timeOffPolicy.accrualTiming === 'first-day') {
            return startDate;
        }

        if (this.timeOffPolicy.accrualTiming === 'last-day') {
            return endDate;
        }

        if (this.timeOffPolicy.accrualTiming === 'next-first-day') {
            return moment(endDate, TimeOffDateFormatDashes).add(1, 'days').format(TimeOffDateFormatDashes);
        }
    }

    setTimeOffType(timeOffTypeId: number): void {
        this.timeOffPolicy.timeOffTypeId = timeOffTypeId;
        const timeOffType = this.timeOffTypes.find(
            (timeOffType) => timeOffType.id === this.timeOffPolicy.timeOffTypeId
        );
        this.timeOffPolicy.timeOffType = timeOffType;
        this.requestRule.timeOffType = timeOffType;
    }

    resetTimeOffPolicyAccrualValues(): void {
        // Need to remove the frequency when the accrual type changes from 'accruing' to 'unlimited'
        this.timeOffPolicy.accrualFrequency = undefined;
        this.timeOffPolicy.resetDateValues(this.timeOffPolicy);
    }

    private setDaysPerYear(): void {
        this.days = +getTime(
            +(this.timeOffPolicy.daysAccruedPerPeriod * this.accrualFrequencies[this.timeOffPolicy.accrualFrequency]),
            this.timeOffPolicy.timeOffType
        ).toFixed(DECIMAL_PLACES);
    }
}
